// File: frontend/src/pages/FitenkoPochta.js
// Version: 1.0 (quick-260912-q2c)
// Date: 2026-09-12
// Назначение: экран `/email-inbox` арендатора fitenko на ОБЩЕМ СЛОЕ. Весь
//             рисунок этого экрана — этот файл и раздел 13 `fitenko-shell.css`,
//             и больше нигде. Это граница измерения цены рисунка, а не
//             украшение оглавления.
//
// 🔴 ЭТО ПЕРЕНОС, А НЕ ПЕРЕДЕЛКА. Ни одна надпись, ручка, обработчик и состояние
//    прежнего экрана не потеряны — тождество множеств по четырём осям судит
//    `priemka-pochty.py` (П1), сторона ДО читается `git show b2515714:` У
//    ИСТОЧНИКА, а не из черновика. Перевод надписей на русский в эту задачу НЕ
//    ВХОДИТ: смена языка и смена слоя — разные утверждения, и слить их значило
//    бы лишить приёмку возможности сказать «потеряно».
//
// 🔴 ПРЕЖНИЙ ЭКРАН НЕ УДАЛЁН И НЕ ТРОНУТ. Маршрут `/email-inbox` общий на
//    ЧЕТЫРЁХ арендаторов (`App.js:123`), а правила `fk-*` живут ТОЛЬКО под
//    `html.fitenko`. Развилка стоит в `EmailTriagePage.js` двумя строками: у
//    других арендаторов путь остаётся прежним побайтно.
//
// 🔴 ШАПКА — `ShapkaBezDeystviya`, И ВЫБОР НЕ ВКУСОВОЙ. Главного действия у
//    экрана НЕТ: действия живут на карточке письма (`Approve & Send`,
//    `Dismiss`), и каждое относится к ОДНОМУ письму, а не ко всему экрану.
//    Поставить «Назад к работе» главным действием справа значило бы предложить
//    человеку УХОД с экрана в виде его главного действия.
//
// 🔴 АДРЕС ВОЗВРАТА БЕРЁТСЯ У ЕДИНСТВЕННОГО ДЕРЖАТЕЛЯ АДРЕСОВ
//    (`config/fitenkoNav.js`, LEGACY_DESTINATIONS), а строкой НЕ пишется.
//    Второй держатель разошёлся бы с первым МОЛЧА — ровно в тот день, когда
//    адрес поменяют в одном месте из двух.
//
// 🔴 НИ ОДНОГО АТРИБУТА `style`. Причина не в чистоплотности: часть перекрытий
//    легаси-слоя опознаёт узел ПО ПОДСТРОКЕ в атрибуте style через
//    `:where([style*="…"])` и бьёт с `!important`. При нуле инлайновых стилей
//    вопрос об их специфичности отпадает ПО ПОСТРОЕНИЮ, а не по оценке.
//
// 🔴 ИМЕНА КЛАССОВ ТОЛЬКО СВОИ, `fk-pochta-*`. Запрещены и ПОЧЕМУ: `btn`,
//    `btn-primary`, `btn-secondary`, `card`, `form-input`, `container`,
//    `page-header`, `results-card`, `no-results`, утилиты `bg-*` и `text-*` —
//    ровно эти имена стоят в перекрытиях `html.fitenko` из `index.css` с
//    `!important` и ПОБЕДИЛИ БЫ общий слой. Пустоту этого пересечения
//    доказывает П3б приёмки, а не обещание в комментарии.
//
// 🔴 ТЕГИ h3, p, label, textarea, button СОХРАНЕНЫ. Гарнитура и цвет заголовка
//    приходят каскадом от `html.fitenko h1..h4`; подмена тега на div изменила бы
//    вид, НИЧЕГО не сказав.
//
// 🔴 ЧЕГО ЭТОТ ПЕРЕНОС НЕ ДОКАЗЫВАЕТ: живой замер на бою НЕ СНЯТ — выкатка
//    условиями задачи запрещена. Доказано «в исходнике и в бандле правило
//    такое», а не «на экране читается».

import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

import apiService from '../services/apiService';
import { ShapkaBezDeystviya, Sostoyanie } from '../components/FitenkoUzor';
import { LEGACY_DESTINATIONS } from '../config/fitenkoNav';
import { useUiLanguage } from '../context/UiLanguageContext';
import { useNavCounters } from '../hooks/useNavCounters';
import { useBranding } from '../context/BrandingContext';

// Адрес по ключу у держателя адресов. Приём взят дословно у соседнего экрана
// слоя (`FitenkoObrashcheniya.js`) — новых приёмов здесь не изобретается.
const adresPoKlyuchu = (kluch) => {
    const d = LEGACY_DESTINATIONS.find((z) => z.key === kluch);
    return d ? d.path : null;
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        return new Date(dateString).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return dateString; }
};

// --- Карточка одного письма ---
const KartochkaPisma = ({ email, onActionDone }) => {
    const { t } = useUiLanguage();
    const { isFitenko } = useBranding();
    const extracted = email.ai_extracted || {};
    const [reply, setReply] = useState(email.ai_draft_reply || '');
    const [working, setWorking] = useState(false);

    const clientEmailMismatch = !isFitenko && Boolean(
        extracted.client_email &&
        email.from_email &&
        extracted.client_email.trim().toLowerCase() !== email.from_email.trim().toLowerCase()
    );

    const handleApprove = async () => {
        setWorking(true);
        try {
            await apiService.approveEmail(email.id, reply);
            toast.success(t('shell.mail.toast_approved'));
            onActionDone(email.id);
        } catch (err) {
            toast.error(t('shell.mail.toast_send_failed', { error: err.message }));
        } finally {
            setWorking(false);
        }
    };

    const handleDismiss = async () => {
        setWorking(true);
        try {
            await apiService.dismissEmail(email.id);
            toast.success(t('shell.mail.toast_dismissed'));
            onActionDone(email.id);
        } catch (err) {
            toast.error(t('shell.mail.toast_dismiss_failed', { error: err.message }));
        } finally {
            setWorking(false);
        }
    };

    const chips = [
        extracted.visa_type && { label: t('shell.mail.chip_visa'), value: extracted.visa_type },
        extracted.urgency && { label: t('shell.mail.chip_urgency'), value: extracted.urgency },
        extracted.language && { label: t('shell.mail.chip_language'), value: extracted.language },
        extracted.client_phone && { label: t('shell.mail.chip_phone'), value: extracted.client_phone },
    ].filter(Boolean);

    return (
        <article className="fk-pochta-card">
            <div className="fk-pochta-head">
                <div className="fk-pochta-ot">
                    <span className="fk-pochta-ot-imya">{email.from_name || email.from_email}</span>
                    {email.from_name && <span className="fk-pochta-ot-adres">&lt;{email.from_email}&gt;</span>}
                </div>
                <span className="fk-pochta-kogda">{formatDate(email.received_at)}</span>
            </div>

            {clientEmailMismatch && (
                <div className="fk-pochta-mismatch-warning" role="alert">
                    {t('shell.mail.mismatched_client_email', {
                        extracted: extracted.client_email,
                        sendingTo: email.from_email
                    })}
                </div>
            )}

            <h3 className="fk-pochta-tema">{email.subject || t('shell.mail.no_subject')}</h3>

            {email.ai_summary && (
                <div className="fk-pochta-svodka">
                    <span className="fk-pochta-svodka-metka">{t('shell.mail.ai_summary')}</span>
                    <p className="fk-pochta-svodka-text">{email.ai_summary}</p>
                </div>
            )}

            {chips.length > 0 && (
                <div className="fk-pochta-metki">
                    {chips.map((c) => (
                        <span key={c.label} className="fk-pochta-metka">
                            <strong>{c.label}:</strong> {c.value}
                        </span>
                    ))}
                </div>
            )}

            <label className="fk-pochta-otvet-metka">
                <span>{t('shell.mail.proposed_reply')}</span>
                <textarea
                    className="fk-pochta-otvet"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={6}
                    placeholder={t('shell.mail.placeholder')}
                />
            </label>

            <div className="fk-pochta-deystviya">
                <button type="button" className="fk-pochta-act-vtor" onClick={handleDismiss} disabled={working}>
                    {working ? t('shell.mail.working') : t('shell.mail.dismiss')}
                </button>
                <button type="button" className="fk-pochta-act" onClick={handleApprove} disabled={working}>
                    {working ? t('shell.mail.sending') : t('shell.mail.approve')}
                </button>
            </div>
        </article>
    );
};

// --- Экран ---
const FitenkoPochta = () => {
    const { t } = useUiLanguage();
    const { features, isFitenko } = useBranding();
    const showSpamNotice = !isFitenko && Boolean(features?.inbox_email);
    const counters = useNavCounters();
    const mailNotConnected = counters.inbox?.reason === 'mail_not_connected';
    const forwardingNotConfigured = (
        counters.inbox?.reason === 'forwarding_not_configured' ||
        features?.inbox_email?.forwarding_configured === false
    );
    const [emails, setEmails] = useState([]);
    const [isLoading, setIsLoading] = useState(!mailNotConnected);

    useEffect(() => {
        if (mailNotConnected) {
            setIsLoading(false);
            return;
        }
        const fetchEmails = async () => {
            try {
                const data = await apiService.getPendingEmails();
                setEmails(Array.isArray(data) ? data : []);
            } catch (error) {
                toast.error(t('shell.mail.toast_load_failed', { error: error.message }));
            } finally {
                setIsLoading(false);
            }
        };
        fetchEmails();
    }, [mailNotConnected, t]);

    const handleActionDone = (emailId) => {
        setEmails(prev => prev.filter(e => e.id !== emailId));
    };

    // 🔴 ТРИ ОТВЕТА РАЗНЫМИ СЛОВАМИ: «грузим», «пусто» и список. Одинаковыми
    //    словами они не говорят — иначе «ещё не спросили» и «спросили, и нет»
    //    стали бы неразличимы.
    return (
        <div className="fk-page fk-pochta">
            <ShapkaBezDeystviya
                nadzagolovok={t('shell.mail.badge')}
                zagolovok={t('shell.mail.title')}
                podstroka={t('shell.mail.subtitle')}
                nazadKuda={adresPoKlyuchu('dashboard')}
                nazadPodpis={t('shell.mail.back')}
            />

            {showSpamNotice ? (
                <p className="fk-pochta-spam-notice">
                    {t('shell.mail.spam_notice')}
                </p>
            ) : null}

            {isLoading ? (
                <Sostoyanie tekst={t('shell.mail.loading')} />
            ) : mailNotConnected ? (
                <Sostoyanie tekst={t('shell.mail.not_connected')} />
            ) : emails.length > 0 ? (
                <div className="fk-pochta-list">
                    {emails.map(email => (
                        <KartochkaPisma key={email.id} email={email} onActionDone={handleActionDone} />
                    ))}
                </div>
            ) : forwardingNotConfigured ? (
                <Sostoyanie tekst={t('shell.mail.forwarding_not_configured')} />
            ) : (
                <Sostoyanie tekst={t('shell.mail.empty')} />
            )}
        </div>
    );
};

export default FitenkoPochta;
